import { Component, OnInit, Input, ViewRef, ViewChild, Type, AfterViewInit, Output, EventEmitter,
	ComponentRef,
	OnDestroy,
	OnChanges} from '@angular/core';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { MatSort, SortDirection } from '@angular/material/sort';
import { merge, Subscription } from 'rxjs';

import { TableDataSource, TableElementWrapper } from './table.datasource';
import { TableColumn } from './table.column';
import { TableColumnContainer } from './table.column.container';

import { DomSanitizer, SafeHtml} from '@angular/platform-browser';
import { DynamicComponent } from './dynamic/dynamic.component';
import { TextComponent } from './text/text.component';
import { WidgetComponent } from './widget/widget.component';
import { ButtonComponentEvent } from './button/button.component';
import { tap } from 'rxjs/operators';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { TableDetailPage } from './table.detail.page';
import { TableElement } from './table.element';
import { TranslateService } from '@ngx-translate/core';
import { TablePaginator } from './table.paginator';
import { CheckboxComponent } from './checkbox/checkbox.component';

export class DataReloadEvent {
	filter: any;
	active: string;
	sortOrder = 'asc' as SortDirection;
	pageNumber = 1;
	pageSize = 10;

	public constructor(_active: string, _sortOrder?: SortDirection, _pageNumber?: number, _pageSize?: number, _filter?: any) {
		this.filter = _filter;
		this.active = _active;
		this.sortOrder = _sortOrder;
		this.pageNumber = _pageNumber;
		this.pageSize = _pageSize;
	}

	public isChanged(condition: DataReloadEvent): boolean {
		if (condition) {
			if (this.filter !== condition.filter
				|| this.active !== condition.active
				|| this.sortOrder !== condition.sortOrder
				|| this.pageSize !== condition.pageSize) {
					return true;
			} else {
				return false;
			}
		} else {
			return true;
		}
	}
}

export class DataRowClickedEvent {
	element: TableElement;
	columns: TableColumn[];

	public constructor(_element: TableElement, _columns: TableColumn[]) {
		this.element = _element;
		this.columns = _columns;
	}
}

export class DataHeaderClickedEvent {
	activeColumn: TableColumn;
	columns: TableColumn[];

	public constructor(_activeColumn: TableColumn, _columns: TableColumn[]) {
		this.activeColumn = _activeColumn;
		this.columns = _columns;
	}
}

export class DataCellClickedEvent {
	element: TableElement;
	columns: TableColumn[];
	activeColumn: TableColumn;

	public constructor(_element: TableElement, _activeColumn: TableColumn, _columns: TableColumn[]) {
		this.element = _element;
		this.activeColumn = _activeColumn;
		this.columns = _columns;
	}
}

@Component({
	selector: 'app-table',
	templateUrl: './table.component.html',
	styleUrls: ['./table.component.scss'],
	providers: [{ 
		provide: MatPaginatorIntl, useClass: TablePaginator 
	}],
	animations: [
		trigger('detailExpand', [
			state('collapsed', style({height: '0px', minHeight: '0'})),
			state('expanded', style({height: '*'})),
			transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
		]),
	],
})
export class TableComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {

	@Input() disabled = false;
	
	dataSource: TableDataSource;
	columnsContainer: TableColumnContainer = new TableColumnContainer([]);
	totalElements = 0;

	expandedElement: any | null;

	activeRowId: string;
	_activeRow: TableElement;
	_tableId: string;
	_reloadEvent: any;

	_columns: TableColumn[];
	@Input() set columns(_columns: TableColumn[]) {
		this._columns = _columns;
	}
	get columns(): TableColumn[] {
		return this._columns;
	}

	@Input() sortBy = "createdDate";
	@Input() sortDir = "desc";
	@Input() pagable: boolean;
	@Input() pageSize: number;
	@Input() pageSizeOptions: number[];
	@Input() detailPageType: Type<TableDetailPage> | null;
	@Input() set activeRow(element: any) {
		if (element) {
			this._activeRow = new TableElementWrapper(element);
		} else {
			this._activeRow = null;
		}
	}
	get activeRow(): any {
		return this._activeRow;
	}

	@ViewChild(DynamicComponent, { static: true }) dynamicComponent: DynamicComponent;

	_paginator: MatPaginator;
	@ViewChild(MatPaginator, { static: false }) set paginator(_paginator: MatPaginator) {
		this._paginator = _paginator;
		if (this._paginator && !this.pageSubscription) {
			this.setPageSubscription();
		}
	}
	get paginator(): MatPaginator {
		return this._paginator;
	}

	_sort: MatSort;
	@ViewChild(MatSort, { static: false }) set sort(_sort: MatSort) {
		this._sort = _sort;

		if (this._sort && !this.sortSubscription) {
			this.setSortSubscription();
		}
	}
	get sort(): MatSort {
		return this._sort;
	}

	@Output() buttonClicked = new EventEmitter<ButtonComponentEvent>();
	_buttonClickState = false;

	@Output() dataReload = new EventEmitter<DataReloadEvent>();
	@Output() dataRowClicked = new EventEmitter<DataRowClickedEvent>();
	@Output() dataRowDblClicked = new EventEmitter<DataRowClickedEvent>();
	@Output() dataHeaderClicked = new EventEmitter<DataHeaderClickedEvent>();
	@Output() dataHeaderCheckChanged = new EventEmitter<DataHeaderClickedEvent>();
	@Output() dataCellClicked = new EventEmitter<DataCellClickedEvent>();
	@Output() opened = new EventEmitter<TableComponent>();
	@Output() closed = new EventEmitter<TableComponent>();

	loading = false;
	sortSubscription: Subscription;
	pageSubscription: Subscription;

	constructor(
		private _sanitizer: DomSanitizer,
		private translateService: TranslateService
	) {
		this.pagable = false;
		this._tableId = 'internalTableId' + Math.random();
		this.dataSource = new TableDataSource();
	}

	ngOnChanges() {
		this.activeRowId = null;
		if (this.columnsContainer.columns) {
			for (const column of this.columnsContainer.columns) {
				if (column.isIdentifier && this.activeRow) {
					this.activeRowId = TableColumn._getData(this.activeRow.getData(), column.column);
					break;
				}
			}
		}

		if (this.sortSubscription) {
			this.sortSubscription.unsubscribe();
		}
		if (this.sort) {
			this.setSortSubscription();
		}
		if (this.pageSubscription) {
			this.pageSubscription.unsubscribe();
		}
		if (this.paginator) {
			this.setPageSubscription();
		}
	}

	setSortSubscription() {
		this.sortSubscription = this.sort.sortChange.subscribe(() => {
			if (this.paginator) {
				this.paginator.pageIndex = 0;
			}
			this.loadDataPage();
		});
	}

	setPageSubscription() {
		this.pageSubscription = this.paginator.page.subscribe((event: { previousPageIndex: number; pageIndex: number; pageSize: number, length: number }) => {
			if (event.previousPageIndex === event.pageIndex) {
				this.paginator.pageIndex = 0;
			}
			this.loadDataPage();
		});
	}

	ngOnInit() {
		this.columnsContainer = new TableColumnContainer(this._columns);
		this.opened.emit(this);
	}

	ngOnDestroy() {
		if (this.pageSubscription) {
			this.pageSubscription.unsubscribe();
		}
		if (this.sortSubscription) {
			this.sortSubscription.unsubscribe();
		}

		this.closed.emit(this);
	}

	ngAfterViewInit() {
	}

	loadDataPage() {
		this.columnsContainer.columns.forEach((column: any) => {
			column.checked = false;
		});
		let sortColumn = this.columnsContainer.getColumn(this.sort.active);

		if (!sortColumn) {
			sortColumn = this.columnsContainer.getColumnByIndex(0);
		}

		if (this.pagable === true) {
			let filter = null;

			if (this._reloadEvent) {
				filter = this._reloadEvent.filter;
			}

			this.dataReload.emit(new DataReloadEvent(sortColumn.sortBy,
				this.sort.direction,
				this.paginator.pageIndex,
				this.paginator.pageSize,
				filter));
		} else {
			this.dataReload.emit(new DataReloadEvent(sortColumn.sortBy,
				this.sort.direction,
				0,
				this.totalElements));
		}
	}

	public refresh(totalElements: number, list: any[], columns?: TableColumn[], $reloadEvent?: DataReloadEvent): void {
		this.loading = true;
		this.totalElements = totalElements;
		this.columnsContainer.setColumns(columns ? this.columns = columns : this.columns);

		if ($reloadEvent) {
			const sortColumn = this.columnsContainer.getColumnBySortBy($reloadEvent.active);

			if (this.paginator) {
				this.paginator.pageIndex = (typeof $reloadEvent.pageNumber === 'undefined' ? this.paginator.pageIndex : $reloadEvent.pageNumber);
				this.paginator.pageSize = (typeof $reloadEvent.pageSize === 'undefined' ? this.paginator.pageSize : $reloadEvent.pageSize);
			}
			if (this.sort) {
				this.sort.active = (sortColumn ? sortColumn.column : ($reloadEvent.active ? $reloadEvent.active : this.sort.active));
				this.sort.direction = ($reloadEvent.sortOrder ? $reloadEvent.sortOrder : this.sort.direction);
				this.sortBy = this.sort.active;
				this.sortDir = this.sort.direction;
			} else {
				this.sortBy = (sortColumn ? sortColumn.column : $reloadEvent.active);
				this.sortDir = ($reloadEvent.sortOrder ? $reloadEvent.sortOrder : (sortColumn ? sortColumn.column : 'asc'));
			}
			this._reloadEvent = $reloadEvent;
		} else {
			if (this.paginator) {
				this.paginator.pageIndex = 0;
			}
		}	

		if (this._activeRow) {
			this.columnsContainer.columns.forEach(column => {
				if (column.isIdentifier) {
					this._activeRow.id(this._activeRow.get(column.column));
				}
			});
		}
		if (list) {
			this.dataSource.loadData(list.map(element => {
				const wrapper = new TableElementWrapper(element);

				this.createComponent(wrapper, this.columnsContainer.columns);
				return wrapper;
			}));
			this.columnsContainer.columns.forEach((column => {
				if (column.type === CheckboxComponent) {
					column.checked = this.dataSource.isAllSelected();
				}
			}));
		}
		this.loading = false;
	}

	sortItems(item1: any, item2: any, sortColumn: TableColumn, condition: DataReloadEvent) {
		if (condition.sortOrder === 'asc') {
			if (!TableColumn._getData(item1, condition.active)) {
				return 1;
			} else if (!TableColumn._getData(item2, condition.active)) {
				return -1;
			} else {
				if (isNaN(TableColumn._getData(item1, condition.active))) {
					return TableColumn._getData(item1, condition.active).localeCompare(TableColumn._getData(item2, condition.active));
				} else {
					return parseFloat(TableColumn._getData(item1, condition.active)) - parseFloat(TableColumn._getData(item2, condition.active));
				}
			}
		} else {
			if (!TableColumn._getData(item1, condition.active)) {
				return -1;
			} else if (!TableColumn._getData(item2, condition.active)) {
				return 1;
			} else {
				if (isNaN(TableColumn._getData(item1, condition.active))) {
					return TableColumn._getData(item2, condition.active).localeCompare(TableColumn._getData(item1, condition.active));
				} else {
					return parseFloat(TableColumn._getData(item2, condition.active)) - parseFloat(TableColumn._getData(item1, condition.active));
				}
			}
		}
	}

	refreshData(results: any[], columns: TableColumn[], condition?: DataReloadEvent, sort?: (array: any[], sortColumn: TableColumn, condition: DataReloadEvent) => any[]) {
		if (results) {
			if (!condition) {
				let activeColumn: TableColumn = null;
				for (const column of columns) {
					if (!column.ignoreSort) {
						activeColumn = column;
						break;
					}
				}
				if (activeColumn) {
					condition = new DataReloadEvent(activeColumn.column, 'desc', 0, this.pageSize);
				} else {
					condition = new DataReloadEvent(columns[columns.length - 1].column, 'desc', 0, this.pageSize);
				}
			}

			let sortColumn = null;

			columns.forEach(column => {
				if (column.column === condition.active) {
					sortColumn = column;
				}
			});

			if (sortColumn) {
				if (sort) {
					results = sort(results, sortColumn, condition);
				} else {
					const self = this;
					results.sort((item1: any, item2: any) => {
						return self.sortItems(item1, item2, sortColumn, condition);
					});
				}
			}

			const totalElements = results.length;
			const elements = [];
			const pageNumber = (condition.pageNumber ? condition.pageNumber : 0);
			const pageSize = (
				this.pagable ? 
					(condition.pageSize ? condition.pageSize : (this.paginator ? this.paginator.pageSize : 10)) : 
					results.length
			);
			const max = Math.min((pageNumber  + 1) * pageSize, totalElements);
			for (let index = pageNumber * pageSize; index < max; index ++) {
				elements.push(results[index]);
			}
			this.refresh(totalElements, elements, columns, condition);
		}
	}

	addData(element: any) {
		const wrapper = new TableElementWrapper(element);

		this.createComponent(wrapper, this.columnsContainer.columns);
		this.dataSource.addData(wrapper);
	}

	removeData(element: any) {
		this.dataSource.removeData(new TableElementWrapper(element));
	}

	reloadData() {
		this.dataSource.reloadData();

		if (this.expandedElement) {
			this.getDetailComponent(this.expandedElement).instance.setContent(this, this.expandedElement, this.columnsContainer.columns);
		}
	}

	setSelectState(selectedElementIds: string[]) {
		this.dataSource.setSelectState(selectedElementIds);

		const isAllSelected = this.dataSource.isAllSelected();
		if (this.columnsContainer.columns && this.columnsContainer.columns.length > 0) {
			this.columnsContainer.columns.forEach((column: any) => {
				if (column.headerCheckbox) {
					column.checked = isAllSelected;
				}
			});
		}

		if (selectedElementIds && selectedElementIds.length > 0) {
			this.activeRowId = selectedElementIds[0];
		}
	}

	resetSelectState() {
		this.dataSource.resetSelectState();
	}

	public transform(v: string): SafeHtml {
		return this._sanitizer.bypassSecurityTrustHtml(v);
	}

	createComponent(element: TableElement, columns: TableColumn[]): void {
		columns.forEach(column => {
			if (column.isIdentifier) {
				element.id(TableColumn._getData(element.getData(), column.column));
			}
		});

		columns.forEach(column => {
			let cellComponent: Type<WidgetComponent> = TextComponent;

			if (column.type && typeof(column.type) !== typeof('')) {

				if (column.checker) {
					if (column.checker(element.getData()) === true) {
						cellComponent = column.type;
					} else {
						if (column.checkerType) {
							cellComponent = column.checkerType;
						} else {
							cellComponent = null;
						}
					}
				} else {
					cellComponent = column.type;
				}
			}

			if (cellComponent) {
				this.dynamicComponent.addComponent(cellComponent, this, element, column);
				const view: ViewRef = this.dynamicComponent.container.detach(0);
	
				this.setView(element, column, view);
	
				this.dynamicComponent.resetContainer();	
			}
		});
	}

	setView(element: TableElement, column: TableColumn, view: ViewRef): void {
		element['view' + column.column] = view;
	}
	setDetailView(element: TableElement, view: ViewRef, detailComponent?: ComponentRef<TableDetailPage>): void {
		const id = element.id();

		if (element['detailView' + id]) {
			element['detailView' + id].destroy();
		}
		element['detailView' + id] = view;
		element['detailComponent' + id] = detailComponent;
	}
	getView(element: TableElement, column: TableColumn): ViewRef {
		return element['view' + column.column];
	}
	getDetailView(element: TableElement): ViewRef {
		return element['detailView' + element.id() ];
	}
	getDetailComponent(element: TableElement): ComponentRef<TableDetailPage> {
		return element['detailComponent' + element.id()];
	}
	getDetailRowStatus(element: TableElement) {
		return (element ? !element['detailView' + element.id()] : true);
	}

	getElements(): TableElement[] {
		return this.dataSource.getData();
	}

	createDetailPage(detailPage: Type<TableDetailPage>, element: TableElement): void {
		const detailComponent = this.dynamicComponent.createComponent(detailPage,
			this, element, this.columnsContainer.columns);
		const view: ViewRef = this.dynamicComponent.container.detach(0);
		this.setDetailView(element, view, detailComponent);
		this.expandedElement = element;
	}

	resetDetailPage(element: TableElement): void {
		this.setDetailView(element, null, null);
		this.expandedElement = null;
	}

	initDetailPage(element: TableElement): void {
		this.setDetailView(element, null, null);
	}

	setRowDblClicked($event: any, element: TableElement): void {
		if ($event.screenX !== 0 || $event.screenY !== 0) {
			this.dataRowDblClicked.emit(new DataRowClickedEvent(element, this.columns));
		}
	}

	setRowClicked($event: any, element: TableElement): void {
		if ($event.screenX !== 0 || $event.screenY !== 0) {
			if (this.detailPageType && this._buttonClickState === false) {
				if (this.expandedElement) {
					this.initDetailPage(this.expandedElement);
				}
				this.expandedElement = this.expandedElement === element ? null : element;

				if (this.expandedElement) {
					if (!this.getDetailView(element) || !(this.getDetailView(element) instanceof this.detailPageType)) {
						this.createDetailPage(this.detailPageType, element);
					}
				}
			}

			if (this._buttonClickState === false) {
				this.activeRow = element.getData();
				this.dataRowClicked.emit(new DataRowClickedEvent(element, this.columns));
			}
			this._buttonClickState = false;
		}
	}

	setHeaderClicked(column: TableColumn): void {
		this.dataHeaderClicked.emit(new DataHeaderClickedEvent(column, this.columns));
	}

	setHeaderCheckerChanged(column: TableColumn): void {
		if (column.checked) {
			this.dataSource.allSelectState();
		} else {
			this.dataSource.resetSelectState();
		}
		
		this.dataHeaderCheckChanged.emit(new DataHeaderClickedEvent(column, this.columns));
	}

	setHeaderCheckerClicked($event: MouseEvent): void {
		$event.stopPropagation();
	}

	setCellClicked($event: any, element: TableElement, column: TableColumn) {
		if ($event.screenX !== 0 || $event.screenY !== 0) {
			this.dataCellClicked.emit(new DataCellClickedEvent(element, column, this.columns));
		}
	}

	onButtonClicked(event: ButtonComponentEvent): void {
		this._buttonClickState = true;
		event.column.checked = this.dataSource.isAllSelected();

		this.buttonClicked.emit(event);
	}

}
